import { useEffect, useState, useMemo } from 'react'
import { useAppStore } from '@store/appStore'
import ShopSelector from '@components/ShopSelector/ShopSelector'
import GestureViewer from '@components/GestureViewer/GestureViewer'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import { DatasetsManifest } from './types/dataset'
import { apiUrl } from './config'

interface AppProps {
  initialShopId?: string | null
}

function App({ initialShopId }: AppProps) {
  const currentShopId = useAppStore((state) => state.currentShopId)
  const availableDatasets = useAppStore((state) => state.availableDatasets)
  const setAvailableShops = useAppStore((state) => state.setAvailableShops)
  const setAvailableDatasets = useAppStore((state) => state.setAvailableDatasets)
  
  // Force re-render when URL changes (for back button navigation)
  const [urlKey, setUrlKey] = useState(0)
  
  // Listen for URL changes to force re-renders
  useEffect(() => {
    const handleUrlChange = () => setUrlKey(prev => prev + 1)
    window.addEventListener('popstate', handleUrlChange)
    window.addEventListener('urlChanged', handleUrlChange)
    return () => {
      window.removeEventListener('popstate', handleUrlChange)
      window.removeEventListener('urlChanged', handleUrlChange)
    }
  }, [])
  
  // Read current URL parameters (reactive to URL changes)
  const effectiveShopId = useMemo(() => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    const currentShopIdFromUrl = currentUrlParams.get('shopid')
    return currentShopId || currentShopIdFromUrl
  }, [currentShopId, urlKey])

  // Load datasets manifest on app mount
  useEffect(() => {
    const loadDatasets = async () => {
      try {
        console.log('📦 [App] Loading datasets manifest...')
        
        // Load from API (Cosmos DB)
        let response = await fetch(apiUrl('/api/datasets'))
        let source = 'API (Cosmos DB)'
        
        if (!response.ok) throw new Error('Failed to load datasets manifest')
        
        const manifest: DatasetsManifest = await response.json()
        console.log(`📦 [App] Manifest loaded from ${source}:`, {
          total: manifest.total_datasets,
          datasets: manifest.datasets.map(d => ({ id: d.id, name: d.name, cameras: d.camera_count }))
        })
        
        setAvailableDatasets(manifest.datasets)
        
        // Populate available shops from datasets
        const shopIds = manifest.datasets.map(dataset => dataset.id)
        console.log('📦 [App] Available shops:', shopIds)
        setAvailableShops(shopIds)
      } catch (error) {
        console.error('❌ [App] Failed to load datasets:', error)
      }
    }
    
    loadDatasets()
  }, [setAvailableDatasets, setAvailableShops])

  // Load initial shop from URL parameter (only after datasets are loaded)
  // Note: GestureViewer will handle the actual loading, this just ensures datasets are ready
  useEffect(() => {
    if (initialShopId && availableDatasets.length > 0) {
      console.log(`🔗 [App] Shop ID detected in URL: ${initialShopId}`)
      console.log(`🔗 [App] Available datasets:`, availableDatasets.map(d => d.id))
      // Don't call loadDataset here - GestureViewer will handle it when it mounts
    }
  }, [initialShopId, availableDatasets])

  return (
    <ErrorBoundary>
      <div className="app">
        {effectiveShopId && availableDatasets.length > 0 ? (
          <GestureViewer shopId={effectiveShopId} />
        ) : (
          <ShopSelector />
        )}
      </div>
    </ErrorBoundary>
  )
}

export default App
